import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Package,
  Receipt,
  RotateCcw,
  ScanBarcode,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Can } from '@/components/Can';
import { useTenant, useCurrentUser } from '@/stores/authStore';
import { formatCompact, formatMoney, fromApi } from '@/lib/money';
import { StatTile } from '../components/StatTile';
import { useDashboard } from '../queries';
import type { Dashboard, SeriesPoint } from '../types';

const RANGES = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
] as const;

/**
 * The dashboard.
 *
 * Information architecture follows the reference product — greeting, hero
 * sales card, KPI rail, Operations, Performance — rendered as a light UI.
 *
 * One request feeds the whole screen. Empty states are treated as a first-class
 * case rather than an afterthought: a brand-new tenant sees zeros everywhere,
 * and a wall of blank axes reads as broken rather than as new.
 */
export function DashboardPage() {
  const tenant = useTenant();
  const user = useCurrentUser();
  const [days, setDays] = useState<number>(30);

  const { data, isPending, isError, refetch } = useDashboard(days);

  const money = (minor: number) =>
    formatMoney(fromApi(minor), {
      symbol: tenant?.currency_symbol,
      decimals: tenant?.currency_decimals ?? 2,
    });

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  return (
    <div className="flex flex-col gap-7">
      {/* --- Greeting ------------------------------------------------- */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-2xl font-semibold tracking-tight">
              {greeting}, {user?.name.split(' ')[0]}
            </h2>
            {!isPending && !isError && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-good-subtle px-2 py-0.5 text-xs font-medium text-good">
                <span className="h-1.5 w-1.5 rounded-full bg-good" aria-hidden />
                Live
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {new Date().toLocaleDateString('en-IN', {
              weekday: 'long',
              day: 'numeric',
              month: 'short',
            })}
          </p>
        </div>

        <Can do="pos.access">
          <Button asChild size="lg">
            <Link to="/pos">
              <ScanBarcode className="h-4 w-4" strokeWidth={2} />
              Open POS
            </Link>
          </Button>
        </Can>
      </div>

      {isError ? (
        <LoadFailed onRetry={() => void refetch()} />
      ) : (
        <DashboardBody
          data={data}
          isPending={isPending}
          money={money}
          days={days}
          onDaysChange={setDays}
          currencySymbol={tenant?.currency_symbol}
        />
      )}
    </div>
  );
}

function DashboardBody({
  data,
  isPending,
  money,
  days,
  onDaysChange,
  currencySymbol,
}: {
  data?: Dashboard;
  isPending: boolean;
  money: (minor: number) => string;
  days: number;
  onDaysChange: (days: number) => void;
  currencySymbol?: string;
}) {
  // Skeletons only on the FIRST load. Switching range keeps the previous
  // window on screen (see useDashboard) so the numbers move rather than the
  // page blanking.
  if (isPending || !data) {
    return <DashboardSkeleton />;
  }

  const { today, window: period, series, operations, inventory, top_products: topProducts } = data;

  const hasSalesToday = today.sale_count > 0;
  const hasSeries = series.some((point) => point.sale_count > 0);
  const hasProducts = topProducts.length > 0;

  // Tenths of a percent on the wire, so the figure stays integral end to end.
  const pct = (tenths: number | null | undefined) =>
    tenths === null || tenths === undefined ? null : tenths / 10;

  const netOfTax = period.net_sales - period.tax;
  const margin = netOfTax > 0 ? `${(((netOfTax - period.cost) / netOfTax) * 100).toFixed(1)}%` : '—';

  return (
    <>
      {/* --- Hero + KPI rail ------------------------------------------ */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="flex flex-col justify-between overflow-hidden lg:col-span-2">
          <div className="p-5">
            <p className="text-xs font-medium uppercase tracking-[0.06em] text-ink-muted">
              Today&apos;s sales
            </p>
            <p className="tabular mt-2 text-[44px] font-semibold leading-none tracking-tight">
              {money(today.net_sales)}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <Chip>
                {today.sale_count} {today.sale_count === 1 ? 'transaction' : 'transactions'}
              </Chip>
              <Chip>{money(today.average_sale)} avg basket</Chip>
              {today.refunds > 0 && <Chip>{money(today.refunds)} refunded</Chip>}
            </div>
          </div>

          <div className="h-32">
            {hasSeries ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="hero-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-brand)" stopOpacity={0.12} />
                      <stop offset="100%" stopColor="var(--color-brand)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip content={<SeriesTooltip money={money} />} />
                  <Area
                    type="monotone"
                    dataKey="net_sales"
                    stroke="var(--color-brand)"
                    strokeWidth={2}
                    fill="url(#hero-fill)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center border-t border-border bg-sunken/40 text-sm text-ink-muted">
                {hasSalesToday
                  ? 'Today is your first day of trading.'
                  : 'Your sales will appear here once you ring up the first one.'}
              </div>
            )}
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <StatTile
            label="Transactions"
            value={String(period.sale_count)}
            delta={pct(period.change.sale_count)}
            icon={Receipt}
            series={series.map((p) => p.sale_count)}
          />
          <StatTile
            label="Avg basket"
            value={money(period.average_sale)}
            delta={pct(period.change.average_sale)}
            icon={UserPlus}
          />
          {/* invertDelta: more refunds is not good news. */}
          <StatTile
            label="Refunds"
            value={money(period.refunds)}
            icon={RotateCcw}
            invertDelta
          />
        </div>
      </div>

      {/* --- Operations ------------------------------------------------ */}
      <section>
        <h3 className="section-rule mb-3 text-[17px] font-semibold tracking-tight">Operations</h3>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <OperationCard
            to="/inventory?low_stock=1"
            icon={AlertTriangle}
            value={String(inventory.summary.low_stock + inventory.summary.out_of_stock)}
            label="Need reordering"
            tone={
              inventory.summary.low_stock + inventory.summary.out_of_stock > 0
                ? 'warning'
                : 'neutral'
            }
          />
          <OperationCard
            to="/products"
            icon={Package}
            value={String(operations.products)}
            label="Active products"
          />
          <OperationCard
            to="/customers?owing=1"
            icon={Banknote}
            value={String(operations.customers_owing)}
            label="Customers owing"
          />
          <OperationCard
            to="/registers"
            icon={Users}
            value={String(operations.open_registers)}
            label={operations.open_registers === 1 ? 'Open register' : 'Open registers'}
          />
        </div>
      </section>

      {/* --- Performance ----------------------------------------------- */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="section-rule text-[17px] font-semibold tracking-tight">Performance</h3>
          <RangeToggle days={days} onChange={onDaysChange} />
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <div className="border-b border-border p-5">
              <h4 className="text-[15px] font-semibold">Net sales</h4>
              <p className="mt-0.5 text-xs text-ink-muted">
                {data.period.from} to {data.period.to}
              </p>
            </div>

            <div className="grid grid-cols-3 divide-x divide-border border-b border-border">
              <Kpi label="Net sales" value={money(period.net_sales)} />
              <Kpi label="Avg basket" value={money(period.average_sale)} />
              <Kpi label="Gross margin" value={margin} />
            </div>

            <div className="h-64 p-5">
              {hasSeries ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series}>
                    <defs>
                      <linearGradient id="net-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-brand)" stopOpacity={0.12} />
                        <stop offset="100%" stopColor="var(--color-brand)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--color-grid)" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="var(--color-axis)"
                      tick={{ fontSize: 11, fill: 'var(--color-ink-muted)' }}
                      tickLine={false}
                      tickFormatter={shortDate}
                      minTickGap={24}
                    />
                    <YAxis
                      stroke="var(--color-axis)"
                      tick={{ fontSize: 11, fill: 'var(--color-ink-muted)' }}
                      tickLine={false}
                      axisLine={false}
                      width={56}
                      tickFormatter={(v: number) => formatCompact(fromApi(v), currencySymbol)}
                    />
                    <Tooltip content={<SeriesTooltip money={money} />} />
                    <Area
                      type="monotone"
                      dataKey="net_sales"
                      stroke="var(--color-brand)"
                      strokeWidth={2}
                      fill="url(#net-fill)"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="No sales in this period yet." />
              )}
            </div>
          </Card>

          <Card>
            <div className="border-b border-border p-5">
              <h4 className="text-[15px] font-semibold">Top products</h4>
              <p className="mt-0.5 text-xs text-ink-muted">By revenue in this period</p>
            </div>

            <div className="h-85 p-5">
              {hasProducts ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topProducts.slice(0, 6)}
                    layout="vertical"
                    margin={{ left: 4, right: 12 }}
                  >
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="product_name"
                      width={104}
                      tick={{ fontSize: 11, fill: 'var(--color-ink-secondary)' }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(name: string) =>
                        name.length > 16 ? `${name.slice(0, 15)}…` : name
                      }
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--color-sunken)' }}
                      formatter={(value) => money(Number(value))}
                    />
                    <Bar dataKey="revenue" radius={[0, 4, 4, 0]} maxBarSize={22}>
                      {topProducts.slice(0, 6).map((product, index) => (
                        <Cell
                          key={product.product_variant_id}
                          fill={`var(--color-series-${(index % 8) + 1})`}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart message="Sell something to see which products earn." />
              )}
            </div>
          </Card>
        </div>
      </section>
    </>
  );
}

function shortDate(iso: string): string {
  const [, month, day] = iso.split('-');
  return `${day}/${month}`;
}

/**
 * Recharts' default tooltip renders raw numbers — which for minor units means
 * "125000" where the user expects "₹1,250.00".
 */
function SeriesTooltip({
  active,
  payload,
  money,
}: {
  active?: boolean;
  payload?: Array<{ payload: SeriesPoint }>;
  money: (minor: number) => string;
}) {
  const point = payload?.[0]?.payload;

  if (!active || !point) return null;

  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-ink-muted">{point.date}</p>
      <p className="tabular mt-1 text-sm font-semibold">{money(point.net_sales)}</p>
      <p className="tabular text-xs text-ink-muted">
        {point.sale_count} {point.sale_count === 1 ? 'sale' : 'sales'}
        {point.refunds > 0 && ` · ${money(point.refunds)} refunded`}
      </p>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="tabular rounded-md bg-sunken px-2.5 py-1 text-xs font-medium text-ink-secondary">
      {children}
    </span>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-5">
      <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-muted">{label}</p>
      <p className="tabular mt-1.5 text-xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}

function OperationCard({
  to,
  icon: Icon,
  value,
  label,
  tone = 'neutral',
}: {
  to: string;
  icon: typeof Package;
  value: string;
  label: string;
  tone?: 'neutral' | 'warning';
}) {
  return (
    <Link
      to={to}
      className="card group flex items-center gap-4 p-4 transition-colors hover:bg-sunken"
    >
      <span
        className={
          tone === 'warning'
            ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-warning-subtle text-[#8a5f00]'
            : 'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand'
        }
      >
        <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <p className="tabular text-2xl font-semibold leading-none tracking-tight">{value}</p>
        <p className="mt-1 truncate text-xs text-ink-muted">{label}</p>
      </div>

      <ArrowRight
        className="h-4 w-4 shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5"
        strokeWidth={2}
        aria-hidden
      />
    </Link>
  );
}

function RangeToggle({ days, onChange }: { days: number; onChange: (days: number) => void }) {
  return (
    <div className="flex items-center gap-0.5 rounded-lg bg-sunken p-0.5">
      {RANGES.map((range) => (
        <button
          key={range.label}
          type="button"
          onClick={() => onChange(range.days)}
          aria-pressed={days === range.days}
          className={
            days === range.days
              ? 'rounded-md bg-surface px-3 py-1.5 text-xs font-medium shadow-sm'
              : 'rounded-md px-3 py-1.5 text-xs font-medium text-ink-secondary hover:text-ink'
          }
        >
          {range.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Empty states matter more than the charts here.
 *
 * A brand-new tenant sees zeros everywhere; a wall of blank axes reads as
 * broken rather than as new.
 */
function EmptyChart({ message }: { message: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center">
      <Package className="h-8 w-8 text-ink-muted" strokeWidth={1.25} aria-hidden />
      <p className="max-w-[220px] text-sm text-ink-muted">{message}</p>
    </div>
  );
}

/**
 * Shaped like the real thing, not a spinner.
 *
 * A layout that appears in place avoids the reflow that makes a dashboard feel
 * slower than it is.
 */
function DashboardSkeleton() {
  return (
    <div className="flex animate-pulse flex-col gap-7" aria-busy="true" aria-label="Loading">
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card h-64 lg:col-span-2" />
        <div className="flex flex-col gap-4">
          <div className="card h-18.5" />
          <div className="card h-18.5" />
          <div className="card h-18.5" />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card h-18.5" />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card h-105 lg:col-span-2" />
        <div className="card h-105" />
      </div>
    </div>
  );
}

/**
 * A failed dashboard is not a failed session.
 *
 * Offering Retry rather than redirecting matters: the most likely cause is a
 * dropped connection in a shop, and signing someone out for that would be
 * both wrong and infuriating.
 */
function LoadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="flex flex-col items-center gap-3 p-10 text-center">
      <AlertTriangle className="h-8 w-8 text-ink-muted" strokeWidth={1.25} aria-hidden />
      <div>
        <p className="font-medium">That didn&apos;t load.</p>
        <p className="mt-1 text-sm text-ink-muted">
          Your data is fine — this screen just couldn&apos;t reach the server.
        </p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}
